// Local date-only helpers for the time-off screens. Everything here works on
// midnight-local Date objects so a range picked on the calendar never shifts a
// day under a UTC conversion.

export const normalizeDateOnly = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

export const countWeekdaysInclusive = (startDate, endDate) => {
  if (!startDate || !endDate || endDate < startDate) return 0;
  let count = 0;
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    const day = cursor.getDay();
    if (day >= 1 && day <= 5) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

export const parseLocalDateOnly = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const formatDateOnly = (d) => {
  const pad2 = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export const keyForDate = (d) => {
  const date = normalizeDateOnly(d);
  if (!date) return '';
  return formatDateOnly(date);
};

export const countDaysInclusive = (startDate, endDate) => {
  if (!startDate || !endDate || endDate < startDate) return 0;
  const a = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const b = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const diff = b.getTime() - a.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
};

export const formatLongDate = (value) => {
  const date = normalizeDateOnly(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

// Every date-only key a close date list covers, from the resolved ranges the
// close-dates endpoint returns.
export const closeDateKeySet = (closeDates) => {
  const keys = new Set();
  for (const closeDate of closeDates || []) {
    const ranges = Array.isArray(closeDate?.resolved_dates) ? closeDate.resolved_dates : [];
    for (const range of ranges) {
      const start = parseLocalDateOnly(range?.startDate);
      const end = parseLocalDateOnly(range?.endDate || range?.startDate);
      if (!start || !end || end < start) continue;
      for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
        keys.add(formatDateOnly(cursor));
      }
    }
  }
  return keys;
};

// Date-only key -> close date name, for every day a close date list covers.
export const closeDateNameMap = (closeDates) => {
  const names = new Map();
  for (const closeDate of closeDates || []) {
    const name = String(closeDate?.name || '').trim() || 'Close date';
    for (const key of closeDateKeySet([closeDate])) {
      if (!names.has(key)) names.set(key, name);
    }
  }
  return names;
};
